import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { emptyLeadForm, getLeadFormErrorMessage, submitLeadForm, leadFormSchema } from "@/lib/leadForms";
import { getString, getStringArray } from "@/lib/pageContent";

interface ContactCTASectionProps {
  content?: Record<string, unknown>;
}

const countryCodes = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+33", label: "🇫🇷 +33" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+966", label: "🇸🇦 +966" },
  { code: "+974", label: "🇶🇦 +974" },
  { code: "+968", label: "🇴🇲 +968" },
  { code: "+973", label: "🇧🇭 +973" },
  { code: "+965", label: "🇰🇼 +965" },
  { code: "+27", label: "🇿🇦 +27" },
  { code: "+234", label: "🇳🇬 +234" },
  { code: "+254", label: "🇰🇪 +254" },
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+63", label: "🇵🇭 +63" },
  { code: "+82", label: "🇰🇷 +82" },
  { code: "+39", label: "🇮🇹 +39" },
  { code: "+34", label: "🇪🇸 +34" },
  { code: "+7", label: "🇷🇺 +7" },
  { code: "+55", label: "🇧🇷 +55" },
  { code: "+52", label: "🇲🇽 +52" },
  { code: "+92", label: "🇵🇰 +92" },
  { code: "+880", label: "🇧🇩 +880" },
  { code: "+94", label: "🇱🇰 +94" },
  { code: "+977", label: "🇳🇵 +977" },
];

const fallbackContent = {
  title: "Ready to Find Clarity in Your Next Move?",
  description:
    "Whether it's a leadership breakthrough for your team or personal coaching for your own growth — it starts here.",
  benefits: ["300+ Coaching Hours", "India & UAE Presence", "7 Professional Certifications", "Clarity Within. Success Beyond."],
  buttonLabel: "Get Started →",
  formTitle: "Contact Us",
  formDescription: "Tell us about your need — Mahirah will respond within 24 hours.",
  submitLabel: "Submit",
  subjectOptions: [
    "Individual Coaching",
    "Corporate Training",
    "Image Consulting",
    "Campus-to-Corporate Training",
    "Handwriting Analysis",
    "ART Therapy for Stress Relief & Confidence",
    "Something Else",
    "General Inquiry",
  ],
};

const ContactCTASection = ({ content }: ContactCTASectionProps) => {
  const { toast } = useToast();
  const [form, setForm] = useState(emptyLeadForm);
  const [countryCode, setCountryCode] = useState("+91");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formTitle = getString(content?.formTitle, fallbackContent.formTitle);
  const formDescription = getString(content?.formDescription, fallbackContent.formDescription);
  const submitLabel = getString(content?.submitLabel, fallbackContent.submitLabel);
  const subjectOptions = getStringArray(content?.subjectOptions, fallbackContent.subjectOptions);

  const handlePhoneChange = (value: string) => {
    const filtered = value.replace(/[^0-9\s-]/g, "");
    setForm({ ...form, phone: filtered });
    if (fieldErrors.phone) setFieldErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    const submissionForm = { ...form, phone: form.phone ? `${countryCode} ${form.phone}` : "" };
    const result = leadFormSchema.safeParse(submissionForm);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0] as string;
        if (!errors[key]) errors[key] = issue.message;
      });
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitLeadForm(submissionForm, "home-contact-cta");
      toast({ title: "Message sent!", description: "Mahirah will get back to you within 24 hours." });
      setForm(emptyLeadForm);
    } catch (error) {
      toast({
        title: "Unable to send message",
        description: getLeadFormErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="w-full bg-secondary py-10">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card p-6 sm:p-8 md:p-12">
          <motion.h3 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center text-2xl font-heading font-bold text-foreground md:text-3xl">{formTitle}</motion.h3>
          <p className="mt-1.5 text-center font-sans text-base text-muted-foreground">{formDescription}</p>
          <form onSubmit={handleSubmit} className="mt-5 space-y-3.5" noValidate>
            <div>
              <Label htmlFor="cta-name">Full Name *</Label>
              <Input id="cta-name" value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: "" })); }} placeholder="Your full name" maxLength={100} disabled={isSubmitting} />
              {fieldErrors.name && <p className="mt-1 text-sm text-destructive">{fieldErrors.name}</p>}
            </div>
            <div>
              <Label htmlFor="cta-email">Email *</Label>
              <Input id="cta-email" type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: "" })); }} placeholder="you@email.com" maxLength={255} disabled={isSubmitting} />
              {fieldErrors.email && <p className="mt-1 text-sm text-destructive">{fieldErrors.email}</p>}
            </div>
            <div>
              <Label htmlFor="cta-phone">Phone Number</Label>
              <div className="flex gap-2">
                <Select value={countryCode} onValueChange={setCountryCode} disabled={isSubmitting}>
                  <SelectTrigger className="h-10 w-[100px] shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countryCodes.map((cc) => (
                      <SelectItem key={cc.code} value={cc.code}>{cc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input id="cta-phone" type="tel" value={form.phone} onChange={(e) => handlePhoneChange(e.target.value)} placeholder="00000 00000" maxLength={15} disabled={isSubmitting} />
              </div>
              {fieldErrors.phone && <p className="mt-1 text-sm text-destructive">{fieldErrors.phone}</p>}
            </div>
            <div>
              <Label>I'm Interested In *</Label>
              <Select value={form.subject} onValueChange={(v) => { setForm({ ...form, subject: v }); if (fieldErrors.subject) setFieldErrors((prev) => ({ ...prev, subject: "" })); }} disabled={isSubmitting}>
                <SelectTrigger><SelectValue placeholder="Select a topic" /></SelectTrigger>
                <SelectContent>
                  {subjectOptions.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.subject && <p className="mt-1 text-sm text-destructive">{fieldErrors.subject}</p>}
            </div>
            <div>
              <Label htmlFor="cta-msg">Message *</Label>
              <Textarea id="cta-msg" value={form.message} onChange={(e) => { setForm({ ...form, message: e.target.value }); if (fieldErrors.message) setFieldErrors((prev) => ({ ...prev, message: "" })); }} placeholder="Tell us about your goals..." maxLength={1000} rows={3} disabled={isSubmitting} />
              {fieldErrors.message && <p className="mt-1 text-sm text-destructive">{fieldErrors.message}</p>}
            </div>
            <Button type="submit" className="w-full bg-accent font-semibold text-accent-foreground hover:bg-accent/90" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : submitLabel}
            </Button>
          </form>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <a href="https://wa.me/917899804670" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 rounded-md bg-accent px-2 py-2.5 text-sm font-medium text-accent-foreground transition-opacity hover:opacity-90"><MessageCircle className="h-3.5 w-3.5" />WhatsApp India</a>
            <a href="https://wa.me/971525409896" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 rounded-md bg-accent px-2 py-2.5 text-sm font-medium text-accent-foreground transition-opacity hover:opacity-90"><MessageCircle className="h-3.5 w-3.5" />WhatsApp UAE</a>
            <a href="mailto:mvibeon@gmail.com" className="flex items-center justify-center gap-1 rounded-md bg-secondary px-2 py-2.5 text-sm font-medium text-secondary-foreground transition-opacity hover:opacity-90"><Mail className="h-3.5 w-3.5" />Email Us</a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactCTASection;
